import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import API_URL from '../config';
import { showError } from '../utils/toast';
import Button from './ui/Button';
import './NCImporter.css';

/**
 * v4.3.0 Feature 1 — Modal de upload de NC recebida de cliente externo.
 *
 * Fluxo:
 *   1. Drag & drop ou click para escolher arquivo (PDF/DOCX/imagem)
 *   2. Validação client-side: ≤ 25MB; warning entre 15-25MB
 *   3. POST /api/v3/nc/importar (multipart) com loading
 *   4. Em sucesso: persiste em sessionStorage (TTL 1h) e chama onExtracted(result)
 *
 * Props:
 *   onClose      — fecha o modal
 *   onExtracted  — callback(result) — result tem dados_extraidos, confianca_extracao, etc.
 */
const MAX_BYTES = 25 * 1024 * 1024;
const WARN_BYTES = 15 * 1024 * 1024;
const SESSION_KEY = 'nc_import_draft';
const TTL_MS = 60 * 60 * 1000; // 1 hora

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
};

function NCImporter({ onClose, onExtracted }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const sizeMB = file ? file.size / (1024 * 1024) : 0;
  const showSizeWarning = file && file.size >= WARN_BYTES && file.size <= MAX_BYTES;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPT,
    multiple: false,
    maxSize: MAX_BYTES,
    onDrop: (accepted, rejected) => {
      if (rejected && rejected.length > 0) {
        const err = rejected[0].errors[0];
        if (err.code === 'file-too-large') {
          showError(`Arquivo excede o limite de 25MB (${(rejected[0].file.size / (1024 * 1024)).toFixed(1)}MB).`);
        } else if (err.code === 'file-invalid-type') {
          showError('Tipo de arquivo não suportado. Use PDF, DOCX, PNG, JPG ou WEBP.');
        } else {
          showError(err.message);
        }
        return;
      }
      if (accepted && accepted.length > 0) {
        setFile(accepted[0]);
      }
    },
  });

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', file);

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/v3/nc/importar`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showError(err.detail || `Erro ${res.status} ao importar arquivo`);
        setLoading(false);
        return;
      }

      const result = await res.json();

      // Persistir em sessionStorage para sobreviver a refresh acidental (TTL 1h)
      try {
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ data: result, expires: Date.now() + TTL_MS, fileName: file.name })
        );
      } catch (e) {
        // sessionStorage cheio ou bloqueado — não impede o fluxo
      }

      if (onExtracted) onExtracted(result, file);
    } catch (err) {
      showError(`Falha de conexão: ${err.message}`);
      setLoading(false);
    }
  };

  return (
    <div className="nc-importer-overlay" onClick={onClose}>
      <div className="nc-importer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nc-importer-header">
          <h3>
            <span className="material-symbols-outlined nc-importer-header-icon">upload_file</span>
            Importar formulário de NC
          </h3>
          <button className="nc-importer-close" onClick={onClose} disabled={loading} aria-label="Fechar">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="nc-importer-body">
          <p className="nc-importer-desc">
            Envie o formulário recebido (PDF, DOCX ou imagem). A IA vai extrair os campos
            para você revisar antes de criar a NC.
          </p>

          <div
            {...getRootProps()}
            className={`nc-importer-dropzone ${isDragActive ? 'active' : ''} ${file ? 'has-file' : ''}`}
          >
            <input {...getInputProps()} />
            {!file && (
              <>
                <div className="nc-importer-icon">
                  <span className="material-symbols-outlined">description</span>
                </div>
                <p>
                  {isDragActive
                    ? 'Solte o arquivo aqui...'
                    : 'Arraste o arquivo ou clique para selecionar'}
                </p>
                <small>PDF · DOCX · PNG · JPG · WEBP — até 25MB</small>
              </>
            )}
            {file && (
              <>
                <div className="nc-importer-file-icon">
                  <span className="material-symbols-outlined">attach_file</span>
                </div>
                <p className="nc-importer-file-name">{file.name}</p>
                <small>{sizeMB.toFixed(2)} MB</small>
              </>
            )}
          </div>

          {showSizeWarning && (
            <div className="nc-importer-warning">
              <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'text-bottom' }}>warning</span>
              {' '}Arquivos grandes podem demorar até 60s para processar.
            </div>
          )}
        </div>

        <div className="nc-importer-footer">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon={loading ? 'hourglass_empty' : 'auto_awesome'}
            onClick={handleUpload}
            disabled={!file || loading}
          >
            {loading ? 'Extraindo com IA...' : 'Extrair campos'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default NCImporter;
